import type { RefObject } from 'preact';
import type { CSSProperties } from 'preact/compat';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { useLayoutEffect } from 'react';
import type { MoveTo, TargetProps } from '../types';

/**
 * This hook dynamically adjusts the popover's position to ensure it remains fully
 * visible within the viewport, preventing clipping or overflow. It tests positions
 * in counter-clockwise order: topCenter → topLeft → leftTop → leftCenter →
 * leftBottom → bottomLeft → bottomCenter → bottomRight → rightBottom →
 * rightCenter → rightTop → topRight.
 */
export function useAutoPositionedRef<T extends HTMLElement>(
  targetRef: TargetProps['ref'],
  moveTo: MoveTo,
  popoverStyle: CSSProperties,
  movedPosition: string,
  isAnchored: boolean
): RefObject<T> {
  const popoverRef = useRef<T>(null);
  const { left, top, right, bottom } = popoverStyle;

  const reposition = useCallback(() => {
    if (!popoverRef.current || !targetRef.current || !isAnchored) return;

    if (isFullElementInsideViewport(popoverRef.current)) {
      return;
    }

    const nextMovement: Record<string, () => void> = {
      topCenter: moveTo.topLeft,
      topLeft: moveTo.leftTop,
      leftTop: moveTo.leftCenter,
      leftCenter: moveTo.leftBottom,
      leftBottom: moveTo.bottomLeft,
      bottomLeft: moveTo.bottomCenter,
      bottomCenter: moveTo.bottomRight,
      bottomRight: moveTo.rightBottom,
      rightBottom: moveTo.rightCenter,
      rightCenter: moveTo.rightTop,
      rightTop: moveTo.topRight
    };

    nextMovement[movedPosition]?.();
  }, [movedPosition, moveTo, isAnchored, targetRef]);

  // Adjust popover position if the default (topCenter) overflows the viewport
  useLayoutEffect(reposition, [left, top, right, bottom, isAnchored]);

  useEffect(() => {
    if (isAnchored) {
      window.addEventListener('resize', moveTo.topCenter);
    }
    return () => window.removeEventListener('resize', moveTo.topCenter);
  }, [isAnchored]);

  const customPopoverRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        popoverRef.current = node as T;
        // Reposition after popover is rendered
        reposition();
      }
    },
    [reposition]
  );

  return customPopoverRef as unknown as RefObject<T>;
}

function isFullElementInsideViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const viewWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewHeight =
    window.innerHeight || document.documentElement.clientHeight;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewHeight &&
    rect.right <= viewWidth
  );
}
